// Central config for the native desktop app download links shown on the login pages.
//
// Per-platform publish flags: a card shows a real download link only when its flag is
// true AND a published version is confirmed live. macOS is signed + notarized and
// published to widgets.weavein.ai. Windows stays gated until the Azure Trusted Signing
// values are set and a signed .exe is published.
//
// VERSION SOURCE OF TRUTH: the login page does NOT hardcode a version. It reads the
// version from the published updater manifest (latest.json), which only advances after
// the release workflow builds, passes CI, and the artifacts are deployed. This prevents
// the login page from advertising a version that isn't actually downloadable yet.
//
// Publishing host: widgets.weavein.ai is a public Vercel static host (the login page is
// pre-auth, so artifacts must be publicly downloadable — a private GitHub Release asset
// would NOT work here). Artifacts are produced by .github/workflows/desktop-release.yml
// and deployed to /downloads/ on that host.

import { useEffect, useState } from 'react';

export const DESKTOP_MACOS_PUBLISHED = true;
// Windows is signed via Azure Artifact (Trusted) Signing and published to
// widgets.weavein.ai as of desktop 1.2.5.
export const DESKTOP_WINDOWS_PUBLISHED = true;

export const DESKTOP_DOWNLOAD_MACOS_URL =
  'https://widgets.weavein.ai/downloads/WeaveIn.dmg';

// Must match the filename published by desktop-release.yml's publish-live job
// (also the NSIS updater artifact referenced in latest.json).
export const DESKTOP_DOWNLOAD_WINDOWS_URL =
  'https://widgets.weavein.ai/downloads/WeaveIn_x64-setup.exe';

/**
 * Download URL with a per-version cache-buster. The original 1.0.x DMG was served
 * with `immutable` caching, so browsers that fetched it keep serving that stale
 * (un-notarized, "damaged") copy forever. A `?v=<version>` query makes each release
 * a distinct URL, forcing a fresh fetch of the current notarized build.
 */
export function macDownloadUrl(version: string | null): string {
  return version
    ? `${DESKTOP_DOWNLOAD_MACOS_URL}?v=${encodeURIComponent(version)}`
    : DESKTOP_DOWNLOAD_MACOS_URL;
}

export function windowsDownloadUrl(version: string | null): string {
  return version
    ? `${DESKTOP_DOWNLOAD_WINDOWS_URL}?v=${encodeURIComponent(version)}`
    : DESKTOP_DOWNLOAD_WINDOWS_URL;
}

export const DESKTOP_LATEST_JSON_URL =
  'https://widgets.weavein.ai/downloads/latest.json';

// Native mobile apps (published to the App Store / Play Store). These are plain
// store links — no CORS/versioning/CI involved. Toggle the published flags to
// show the cards as Available with a link.
export const MOBILE_IOS_PUBLISHED = true;
export const MOBILE_ANDROID_PUBLISHED = true;

export const IOS_APP_URL = 'https://apps.apple.com/us/app/weavein/id6763516704';
export const ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=trust.weavein.care';

/**
 * Fetch the version of the currently-published desktop build from the live updater
 * manifest. Returns null if nothing is published or the manifest is unreachable.
 */
export async function fetchPublishedDesktopVersion(): Promise<string | null> {
  try {
    const res = await fetch(DESKTOP_LATEST_JSON_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === 'string' && data.version ? data.version : null;
  } catch {
    return null;
  }
}

/**
 * React hook: resolves to the published desktop version (e.g. "1.0.8"), or null while
 * loading / if nothing is published. Used to gate the download card and label so the
 * login page only ever shows a deployed, CI-passed version.
 */
export function usePublishedDesktopVersion(): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    fetchPublishedDesktopVersion().then((v) => {
      if (active) setVersion(v);
    });
    return () => {
      active = false;
    };
  }, []);
  return version;
}
